(() => {
  if (window.FPProductOptions) return;
  const groupSelector = '[data-fp-option-group]';
  const propertySelector = '[data-fp-option-property]';
  const normalize = value => String(value || '').normalize('NFC').trim();
  const identity = value => normalize(value).toLocaleLowerCase('pt-BR');
  const stateKey = name => `custom:${identity(name)}`;
  const reserved = new Set(['Largura', 'Altura', 'Lado da cordinha', 'Acionamento', 'Acabamento', 'Referente a persiana'].map(identity));
  const main = root => root?.matches?.('.section-main-product') ? root
    : root?.closest?.('.section-main-product') || root?.querySelector?.('.section-main-product') || root;

  function collect(root) {
    const section = main(root);
    const form = section?.querySelector('product-form form.form-product');
    const occupied = new Set(reserved);
    form?.querySelectorAll('[name^="properties["]').forEach(input => {
      if (!input.matches(propertySelector)) occupied.add(identity(input.name.slice(11, -1)));
    });
    const groups = [...(section?.querySelectorAll(groupSelector) || [])].map(element => {
      const name = normalize(element.dataset.propertyName);
      const options = [...element.querySelectorAll('[data-option-slot]')];
      const selected = options.find(option => option.checked && !option.disabled);
      const values = options.map(option => normalize(option.value));
      let error = '';
      if (!name || /^_/.test(name) || /[\[\]\u0000-\u001f]/.test(name)) error = 'Defina um nome válido para a informação no carrinho.';
      else if (occupied.has(identity(name))) error = `O nome “${name}” já é usado por outra informação do produto.`;
      else if (options.length < 2 || options.some(option => !normalize(option.dataset.optionLabel) || !normalize(option.value))) error = `Preencha os nomes e valores das opções de ${name}.`;
      else if (new Set(values.map(identity)).size !== options.length) error = `Use valores diferentes nas opções de ${name}.`;
      else if (element.dataset.defaultOption !== 'none' && !options.some(option => option.dataset.optionSlot === element.dataset.defaultOption)) error = `Escolha uma opção inicial disponível para ${name}.`;
      else if ([...(form?.querySelectorAll(propertySelector) || [])].filter(input => input.dataset.fpOptionProperty === element.dataset.fpOptionGroup).length !== 1) error = `Recarregue a página para confirmar as opções de ${name}.`;
      return { element, name, options, selected, value: selected ? normalize(selected.value) : '', error, required: element.dataset.required === 'true' };
    });
    const names = new Map();
    groups.forEach(group => {
      const key = identity(group.name);
      if (!names.has(key)) names.set(key, []);
      names.get(key).push(group);
    });
    names.forEach(matches => {
      if (matches.length > 1) matches.forEach(group => { group.error = `Use um nome no carrinho exclusivo para ${group.name || 'cada bloco'}.`; });
    });
    groups.forEach(group => {
      group.message = group.error || (group.required && !group.selected ? `Selecione uma opção de ${group.name}.` : '');
    });
    return { section, form, groups, valid: groups.every(group => !group.message) };
  }

  function sync(root) {
    const result = collect(root);
    const byId = new Map(result.groups.map(group => [group.element.dataset.fpOptionGroup, group]));
    result.form?.querySelectorAll(propertySelector).forEach(input => {
      const group = byId.get(input.dataset.fpOptionProperty);
      if (group) input.name = `properties[${group.name}]`;
      input.value = group?.value || '';
      input.disabled = !result.valid || !group?.selected;
    });
    result.groups.forEach(group => {
      const error = group.element.querySelector('[data-fp-option-error]');
      if (error) { error.textContent = group.message; error.hidden = !group.message; }
      group.element.querySelector('fieldset')?.setAttribute('aria-invalid', group.message ? 'true' : 'false');
      const clear = group.element.querySelector('[data-fp-option-clear]');
      if (clear) clear.hidden = !group.selected;
    });
    return result;
  }

  // Variant selectors retain ownership of availability. This gate adds only
  // custom-option validity and never changes the selected variant or its price.
  function gate(root, baseReady) {
    const section = main(root);
    if (!section?.querySelector(`${groupSelector}, ${propertySelector}`)) return;
    const result = sync(section);
    const productForm = result.form?.closest('product-form');
    if (!productForm) return;
    const button = result.form.querySelector('[type="submit"]');
    if (productForm.fpOptionsInitialDisabled === undefined) productForm.fpOptionsInitialDisabled = Boolean(button?.disabled);
    if (typeof baseReady === 'boolean') productForm.dataset.fpBaseReady = String(baseReady);
    let available = productForm.dataset.fpBaseReady === 'true';
    if (productForm.dataset.fpBaseReady === undefined) {
      const picker = section.querySelector('variant-selects');
      available = productForm.dataset.cbb !== 'true' && !productForm.fpOptionsInitialDisabled && (picker ? Boolean(picker.hasConfirmedSelection?.()) : productForm.dataset.fpBaseAvailable === 'true');
    }
    const ready = available && result.valid && !productForm.inFlight && !productForm.cartUncertain && button?.getAttribute('aria-disabled') !== 'true';
    if (button) button.disabled = !ready;
    result.form.querySelectorAll('[data-fp-dynamic-checkout]').forEach(checkout => {
      checkout.inert = !ready;
      checkout.style.visibility = ready ? '' : 'hidden';
    });
    return ready;
  }

  function guard(root, data) {
    const result = sync(root);
    const invalid = result.groups.find(group => group.message);
    if (invalid) {
      invalid.options[0]?.focus();
      throw new Error(invalid.message);
    }
    if (data) {
      result.form?.querySelectorAll(propertySelector).forEach(input => data.delete(input.name));
      result.groups.forEach(group => {
        if (group.selected) data.set(`properties[${group.name}]`, group.value);
      });
    }
    return result;
  }

  function capture(root = document.querySelector('#MainContent')) {
    return Object.fromEntries(collect(root).groups.filter(group => !group.error).map(group => [stateKey(group.name), group.value]));
  }

  function restore(selection, root = document.querySelector('#MainContent')) {
    const saved = selection?.selectors || {};
    const result = collect(root);
    result.groups.forEach(group => {
      const key = stateKey(group.name);
      if (group.error || !Object.prototype.hasOwnProperty.call(saved, key)) return;
      const value = saved[key];
      const match = group.options.find(option => !option.disabled && normalize(option.value) === value);
      const fallback = group.options.find(option => option.dataset.optionSlot === group.element.dataset.defaultOption);
      const choice = match || (value === '' && !group.required ? null : fallback);
      group.options.forEach(option => { option.checked = option === choice; });
    });
    gate(result.section);
  }

  window.FPProductOptions = { collect, sync, gate, guard, capture, restore };
  customElements.define('fp-option-selector', class extends HTMLElement {
    connectedCallback() {
      this.section = main(this);
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.addEventListener('change', () => gate(this.section), { signal: this.listeners.signal });
      this.querySelector('[data-fp-option-clear]')?.addEventListener('click', () => {
        this.querySelectorAll('[data-option-slot]').forEach(option => { option.checked = false; });
        gate(this.section);
      }, { signal: this.listeners.signal });
      queueMicrotask(() => { if (this.isConnected) gate(this.section); });
    }
    disconnectedCallback() {
      this.listeners?.abort();
      const section = this.section;
      queueMicrotask(() => { if (section?.isConnected) gate(section); });
    }
  });

  // Accelerated checkout may bypass the theme's submit handler. Synchronize and
  // validate the same in-form properties before its first pointer/key action.
  function guardCheckout(event) {
    const checkout = event.target.closest?.('[data-fp-dynamic-checkout]');
    const section = checkout && main(checkout);
    if (!section?.querySelector(groupSelector)) return;
    try {
      const form = checkout.closest('product-form');
      if (!gate(section) || typeof form?.capture !== 'function') throw new Error('Confira as opções do produto antes de continuar.');
      form.capture();
    } catch (error) {
      event.preventDefault();
      event.stopImmediatePropagation();
      checkout.closest('product-form')?.handleErrorMessage(error.message);
    }
  }
  document.addEventListener('pointerdown', guardCheckout, true);
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' || event.key === ' ') guardCheckout(event);
  }, true);
})();
